import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ArticleRow, CustomerRow, PeriodQuery, SalesReport, SalespersonRow } from "../../api/reports";
import { reportsApi } from "../../api/reports";
import { formatNumber } from "../../lib/format";
import type { useChartPalette } from "../../theme/useChartPalette";
import { StabloRegistar } from "../../components/reports/StabloRegistar";
import type { StabloRed, StabloSort } from "../../components/reports/StabloRegistar";
import { useToast } from "../../components/Toast";
import { th } from "./stil";

type Props = {
  podaci: SalesReport;
  upit: PeriodQuery;
  locale: string;
  palette: ReturnType<typeof useChartPalette>;
  ucitava: boolean;
};

/**
 * Prodaja po komercijalisti, sa spuštanjem do kupca i do artikla.
 *
 * <p>Opseg putuje sa spuštanjem: spisak kupaca otvoren iz jednog čoveka nosi samo njegove kupce,
 * i zemlja i komercijalista se SEKU a ne sabiraju (`SalesDrillIT`). Komercijalista bez imena nosi
 * prazan ključ, pa se i on šalje — prazno nije „bez filtera".
 *
 * <p>Naplata stoji samo na nivou komercijaliste. Niži nivoi je NE dobijaju i to se vidi: tačka
 * umesto crte, jer crta znači nulu. Uz to, naplata se ne filtrira po zemlji — knjiženje je na
 * kontu kupca i zemlju ne nosi — pa kad je filter zemlje uključen, to piše nad tabelom.
 */
export default function KomercijalistiTab({ podaci, upit, locale, palette, ucitava }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [otvorene, setOtvorene] = useState<Set<string>>(new Set());
  const [kupci, setKupci] = useState<Record<string, CustomerRow[]>>({});
  const [artikli, setArtikli] = useState<Record<string, ArticleRow[]>>({});
  const [dovlaci, setDovlaci] = useState<string | null>(null);
  const [sort, setSort] = useState<StabloSort>(null);
  const [opadajuce, setOpadajuce] = useState(true);

  const ukupanRuc = podaci.totals.ruc.current;
  /**
   * Da li se iz ovog perioda sme čitati rast ili pad.
   *
   * Netačno kad period prelazi presek izvora: septembar 2026 nosi 22 dana a poredi se sa punim
   * mesecom. Oba broja ostaju na ekranu, smer se ne piše.
   */
  const tumaci = podaci.period.coverage == null || podaci.period.coverage.current === "FULL";
  const bezImena = t("report.noSalesperson", "bez komercijaliste");

  // Grafikon poredi količinu i RUC po čoveku: dve veličine koje se ne kreću zajedno, i upravo to
  // je ono što se iz tabele ne vidi — ko prodaje mnogo a nosi malo marže.
  const podatak = useMemo(
    () =>
      podaci.salespeople
        .filter((k) => k.quantity.current !== 0 || k.ruc.current !== 0)
        .map((k) => ({ ime: k.name || bezImena, kolicina: k.quantity.current, ruc: k.ruc.current })),
    [bezImena, podaci.salespeople],
  );

  const sortiraj = useCallback(
    (k: StabloSort) => {
      if (k === sort) setOpadajuce((o) => !o);
      else {
        setSort(k);
        setOpadajuce(k !== "ime");
      }
    },
    [sort],
  );

  const otvori = useCallback(
    async (kljuc: string) => {
      const bio = otvorene.has(kljuc);
      setOtvorene((pre) => {
        const sled = new Set(pre);
        if (bio) sled.delete(kljuc);
        else sled.add(kljuc);
        return sled;
      });
      if (bio) return;

      // Ključ je putanja kroz nivoe, razdvojena znakom koji ni ime ni šifra ne mogu da nose.
      const deo = kljuc.split("\u0000");
      const neuspeh = (e: unknown) => {
        toast.error((e as Error).message);
        setOtvorene((pre) => {
          const sled = new Set(pre);
          sled.delete(kljuc);
          return sled;
        });
      };

      if (deo.length === 1) {
        if (kupci[kljuc]) return;
        setDovlaci(kljuc);
        try {
          // Filter zemlje, ako je uključen, ide zajedno sa čovekom: dva opsega se SEKU.
          const o = await reportsApi.salesCustomers(upit, upit.country ?? null, deo[0]);
          // Spor odgovor ne sme da sleti pod čoveka kog je neko u međuvremenu otvorio.
          if ((o.salesperson ?? "") === deo[0]) setKupci((pre) => ({ ...pre, [kljuc]: o.customers }));
        } catch (e) {
          neuspeh(e);
        } finally {
          setDovlaci((k) => (k === kljuc ? null : k));
        }
        return;
      }

      if (deo.length === 2) {
        if (artikli[kljuc]) return;
        setDovlaci(kljuc);
        try {
          const o = await reportsApi.salesCustomerArticles(upit, deo[1], upit.country ?? null, deo[0]);
          if (o.erpCustomerId === deo[1] && (o.salesperson ?? "") === deo[0]) {
            setArtikli((pre) => ({ ...pre, [kljuc]: o.articles }));
          }
        } catch (e) {
          neuspeh(e);
        } finally {
          setDovlaci((k) => (k === kljuc ? null : k));
        }
      }
    },
    [artikli, kupci, otvorene, toast, upit],
  );

  const stablo: StabloRed[] = useMemo(
    () =>
      poredjaj(podaci.salespeople, sort, opadajuce).map((k) => {
        const kljucK = k.name;
        const uKupce = kupci[kljucK];
        return {
          kljuc: kljucK,
          ime: k.name || bezImena,
          udeo: udeo(k.ruc.current, ukupanRuc),
          mere: [
            { tumaci, sada: k.ruc.current, pre: k.ruc.previous },
            { tumaci, sada: k.quantity.current, pre: k.quantity.previous, jedinica: "kg", decimala: 0 },
            { tumaci, sada: k.salesValue.current, pre: k.salesValue.previous },
            { tumaci, sada: k.collected.current, pre: k.collected.previous },
          ],
          mozeDaSeOtvori: true,
          prazno: t("report.customers.empty", "Za ovaj opseg nema kupaca u periodu."),
          deca: uKupce?.map((c) => {
            const kljucC = `${kljucK}\u0000${c.erpId}`;
            const uArtikle = artikli[kljucC];
            return {
              kljuc: kljucC,
              ime: c.name || c.erpId,
              udeo: udeo(c.ruc.current, ukupanRuc),
              mere: [
                { tumaci, sada: c.ruc.current, pre: c.ruc.previous },
                { tumaci, sada: c.quantity.current, pre: c.quantity.previous, jedinica: "kg", decimala: 0 },
                { tumaci, sada: c.salesValue.current, pre: c.salesValue.previous },
                // Naplata se vodi po komercijalisti; kupac je ne nosi, a nula bi bila tvrdnja.
                { tumaci, sada: null, vanNivoa: true },
              ],
              mozeDaSeOtvori: true,
              prazno: t("report.articles.empty", "Za ovu grupu nema artikala u periodu."),
              deca: uArtikle?.map((a) => ({
                kljuc: `${kljucC}\u0000${a.code}`,
                ime: a.name,
                oznaka: a.code,
                udeo: udeo(a.ruc.current, ukupanRuc),
                mere: [
                  { tumaci, sada: a.ruc.current, pre: a.ruc.previous },
                  { tumaci, sada: a.quantity.current, pre: a.quantity.previous, jedinica: "kg", decimala: 0 },
                  { tumaci, sada: a.salesValue.current, pre: a.salesValue.previous },
                  { tumaci, sada: null, vanNivoa: true },
                ],
              })),
            };
          }),
        };
      }),
    [artikli, bezImena, kupci, opadajuce, podaci.salespeople, sort, t, ukupanRuc],
  );

  const naplata = useMemo(
    () => podaci.salespeople.reduce((a, k) => a + (k.collected.current ?? 0), 0),
    [podaci.salespeople],
  );
  const naplataPre = useMemo(
    () => podaci.salespeople.reduce((a, k) => a + (k.collected.previous ?? 0), 0),
    [podaci.salespeople],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: ucitava ? 0.55 : 1 }}>
      <section className="card" style={{ padding: 14 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
          {t("report.chart.salespeople", "Količina i RUC po komercijalisti")}
        </h3>
        <div className="sub" style={{ marginBottom: 10 }}>
          {t(
            "report.chart.salespeopleHint",
            "Dve veličine koje se ne kreću zajedno: ko prodaje mnogo ne nosi nužno i najveću maržu.",
          )}
        </div>
        <ResponsiveContainer width="100%" height={Math.max(260, podatak.length * 34)}>
          <BarChart data={podatak} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="ime"
              tick={{ fontSize: 11, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
              width={170}
            />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              labelStyle={{ color: palette.inkStrong }}
              formatter={(value, name) => [formatNumber(Number(value), locale), String(name)]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="kolicina"
              name={t("report.col.quantity", "Količina")}
              fill={palette.inkMuted}
              radius={[0, 3, 3, 0]}
            />
            <Bar dataKey="ruc" name={t("report.col.ruc", "RUC")} radius={[0, 3, 3, 0]}>
              {podatak.map((k) => (
                <Cell key={k.ime} fill={k.ruc < 0 ? "var(--warn)" : palette.brand} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {podaci.collectionKinds.length > 0 && (
        <section className="card" style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
            {t("report.collections.title", "Naplata po vrsti knjiženja")}
          </h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t("report.collections.hint", "Zatvaranje avansa nije naplata i ne ulazi: avans se vodi na svom kontu, pa je njegovo zatvaranje preknjižavanje pretplate.")}
          </div>
          <div
            className="kpi-grid"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", marginBottom: 0 }}
          >
            {podaci.collectionKinds.map((v) => (
              <div
                key={v.kind}
                className="card"
                style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}
              >
                <span style={th}>{t(`report.collections.kind.${v.kind}`, v.kind)}</span>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "var(--ink)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatNumber(v.amount, locale)}
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-4)" }}>
                  {t("report.collections.entries", { count: v.entries, defaultValue: "{{count}} knjiženja" })}
                </div>
              </div>
            ))}
          </div>
          {/* Izvor se imenuje na mestu gde se čita.
              Ovde je do 12.10.2026 stajalo „zbir je iz knjige i tačan, raspodela po komercijalisti
              je naša jer pravilo nije poznato". Oboje je bilo netačno, i oboje je zatvoreno time
              što portal sada čita ERP-ov obračun: on reprodukuje kontrolni izveštaj, a raspodelu
              daje sam, jer je za kupca zadužena jedna osoba. */}
          <div className="sub" style={{ marginTop: 10, fontStyle: "italic" }}>
            {t(
              "report.collections.ourAttribution",
              "Naplata dolazi iz ERP-ovog obračuna i reprodukuje kontrolni izveštaj; raspodela po komercijalisti je ERP-ova, jer je za kupca zadužena jedna osoba.",
            )}
          </div>
        </section>
      )}

      <section className="card" style={{ padding: 0 }}>
        {upit.country ? (
          // Ograda stoji NAD tabelom u kojoj se čita, ne u dokumentaciji: red bi inače glasio
          // „ovoliko je naplatio u ovoj zemlji", a to nije ono što knjiga kaže.
          <div className="sub" style={{ padding: "10px 14px 0", fontStyle: "italic" }}>
            {t(
              "report.collections.countryIgnored",
              "Filter zemlje ne važi za kolonu Naplata: knjiženje stoji na kontu kupca i zemlju ne nosi, pa je naplata prikazana za ceo period.",
            )}
          </div>
        ) : null}
        {podaci.salespeople.length === 0 ? (
          <div className="empty" style={{ padding: 18 }}>
            {t("report.emptySalespeople", "Za izabrani period nema prodaje po komercijalisti.")}
          </div>
        ) : (
          <StabloRegistar
            kolone={[
              t("report.col.salesperson", "Komercijalista"),
              t("report.col.ruc", "RUC"),
              t("report.col.quantity", "Količina"),
              t("report.col.value", "Vrednost"),
              t("report.col.collected", "Naplata"),
            ]}
            naslovUdela={t("report.col.shareRuc", "Udeo RUC")}
            sort={sort}
            opadajuce={opadajuce}
            naSortiranje={sortiraj}
            redovi={stablo}
            otvoreni={otvorene}
            naOtvaranje={otvori}
            ucitava={dovlaci}
            zbir={{
              kljuc: "__ukupno",
              ime: t("report.total", "Ukupno"),
              udeo: ukupanRuc === 0 ? null : 100,
              mere: [
                { tumaci, sada: podaci.totals.ruc.current, pre: podaci.totals.ruc.previous },
                {
                  sada: podaci.totals.quantity.current,
                  pre: podaci.totals.quantity.previous,
                  jedinica: "kg",
                  decimala: 0,
                },
                { tumaci, sada: podaci.totals.salesValue.current, pre: podaci.totals.salesValue.previous },
                // Zbir naplate se računa IZ REDOVA koji se vide: zbir koji ne odgovara onome što
                // stoji pod njim gori je od nepostojećeg.
                { tumaci, sada: naplata, pre: naplataPre },
              ],
            }}
            locale={locale}
          />
        )}
      </section>
    </div>
  );
}

/** Udeo u ukupnoj marži; bez ukupnog nema udela, a nula nije odgovor. */
function udeo(deo: number, ukupno: number): number | null {
  if (!ukupno) return null;
  return (deo / ukupno) * 100;
}

/** Ređa komercijaliste po izabranoj koloni; vraća NOV niz, da se zatečeni redosled može vratiti. */
function poredjaj(ljudi: SalespersonRow[], sort: StabloSort, opadajuce: boolean): SalespersonRow[] {
  if (sort === null) return ljudi;
  const znak = opadajuce ? -1 : 1;
  if (sort === "ime") return [...ljudi].sort((a, b) => znak * a.name.localeCompare(b.name));
  // Redosled mera je redosled kolona: 0 RUC, 1 količina, 2 vrednost, 3 naplata.
  const mera = (k: SalespersonRow) =>
    sort === 0
      ? k.ruc.current
      : sort === 1
        ? k.quantity.current
        : sort === 2
          ? k.salesValue.current
          : k.collected.current;
  return [...ljudi].sort((a, b) => znak * (mera(a) - mera(b)));
}
